import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ShapeProps, ShapeRef } from './types';
import { type Object3D } from 'three';
import { sum } from 'lodash';
import { useFrame } from '@react-three/fiber';
import { degToRad } from 'three/src/math/MathUtils';

const RING_COUNT = 3;
const RADIUS = 0.45;

export const ShapeSpinningRings = forwardRef<ShapeRef, ShapeProps>(
  ({ dotCount }, ref) => {
    const targetObjectsRef = useRef<Object3D[]>([]);
    const ringObjectsRef = useRef<(Object3D | null)[]>([]);

    const dotsPerRing: number[] = new Array(RING_COUNT).fill(
      Math.floor(dotCount / RING_COUNT)
    );
    dotsPerRing[dotsPerRing.length - 1] += dotCount % RING_COUNT;

    useImperativeHandle(ref, () => ({
      dotTargets: targetObjectsRef.current,
    }));

    useFrame(
      (
        // @ts-expect-error Using 2nd argument
        state,
        delta
      ) => {
        ringObjectsRef.current.forEach((ringObject) => {
          ringObject?.rotateZ(0.5 * delta);
        });
      }
    );

    return (
      <group
        rotation-x={degToRad(20)}
        rotation-y={degToRad(25)}
        rotation-z={degToRad(-10)}
        position={[0, 0, -RADIUS]}
      >
        {dotsPerRing.map((dotCount, ringIndex) => {
          const startHueIndex =
            ringIndex === 0 ? 0 : sum(dotsPerRing.slice(0, ringIndex));

          return (
            <group
              key={ringIndex}
              rotation-x={
                degToRad(ringIndex === 0 ? 90 : 0) +
                (ringIndex === 2 ? degToRad(-20) : 0)
              }
              rotation-y={
                degToRad(ringIndex === 1 ? 90 : 0) +
                (ringIndex === 2 ? degToRad(-20) : 0)
              }
              rotation-z={degToRad(ringIndex === 2 ? 90 : 0)}
              ref={(ringObject) => {
                ringObjectsRef.current[ringIndex] = ringObject;
              }}
            >
              {new Array(dotCount).fill(null).map((_, ringDotIndex) => (
                <group
                  key={ringDotIndex}
                  position={[
                    Math.sin(
                      (Math.PI * 2 * ringDotIndex) / dotsPerRing[ringIndex]
                    ) * RADIUS,
                    Math.cos(
                      (Math.PI * 2 * ringDotIndex) / dotsPerRing[ringIndex]
                    ) * RADIUS,
                    0,
                  ]}
                  ref={(mesh) => {
                    if (!mesh) return;

                    targetObjectsRef.current[startHueIndex + ringDotIndex] =
                      mesh;
                  }}
                />
              ))}
            </group>
          );
        })}
      </group>
    );
  }
);

ShapeSpinningRings.displayName = 'ShapeSpinningRings';
